import type { ListBuilderColumn, ListBuilderConfiguration } from '../types';

/**
 * Quote status value mapping for info badge display
 */
const QUOTE_STATUS_MAPPING = [
	{
		value: 'Quote - Needs Review',
		displayText: 'Needs Review',
		color: { type: 'warning' },
	},
	{
		value: '13',
		displayText: '',
	},
];

/**
 * Shared column configuration for quote lists
 */
const createQuoteColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'quote-info',
		fields: [
			{
				id: 'quote-number',
				link: {
					type: 'object' as const,
					enabled: true,
					objectTable: 'order',
				},
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: '_id',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text' as const,
			},
		],
		headers: [{ id: 'h1', text: 'Quote #' }],
		sortable: true,
		alignment: 'left' as const,
		verticalAlignment: 'top' as const,
	},
	{
		id: 'date-info',
		fields: [
			{
				id: 'created-date',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'createdDate',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textPrimary',
					},
					formatOptions: { dateFormat: 'datetime-medium' },
				},
				displayStyle: 'date' as const,
			},
			{
				id: 'date-line-break',
				dataConfig: {
					type: 'custom_text' as const,
					customText: '',
				},
				styleConfig: {
					size: 'md' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'closed-date',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'expirationDate',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'date' as const,
			},
		],
		headers: [
			{ id: 'h3', text: 'Created' },
			{ id: 'h4', text: 'Closed' },
		],
		sortable: true,
		alignment: 'left' as const,
		verticalAlignment: 'top' as const,
	},
	{
		id: 'info-column',
		fields: [
			{
				id: 'needs-review-badge',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'status',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'value-mapping' as const,
						valueMapping: [
							{
								value: 'Quote - Needs Review',
								color: { type: 'warning' },
							},
						],
					},
					valueMapping: QUOTE_STATUS_MAPPING,
				},
				displayStyle: 'badge' as const,
			},
		],
		headers: [{ id: 'h9', text: 'Info' }],
		sortable: false,
		alignment: 'center' as const,
		verticalAlignment: 'middle' as const,
	},
	{
		id: 'sales-rep-info',
		fields: [
			{
				id: 'sales-rep-name',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'sales_rep_name',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text' as const,
			},
		],
		headers: [{ id: 'h4', text: 'Sales Rep' }],
		sortable: true,
		alignment: 'left' as const,
		verticalAlignment: 'top' as const,
	},
	{
		id: 'lines-info',
		fields: [
			{
				id: 'number-of-lines',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'numberOfLines',
						objectTable: 'order',
					},
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text' as const,
			},
		],
		headers: [{ id: 'h5', text: 'Lines' }],
		sortable: true,
		alignment: 'center' as const,
		verticalAlignment: 'top' as const,
	},
	{
		id: 'total-info',
		fields: [
			{
				id: 'subtotal-amount',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'subtotal',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'currency' as const,
			},
			{
				id: 'line-break',
				dataConfig: {
					type: 'custom_text' as const,
					customText: '',
				},
				styleConfig: {
					size: 'md' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'total-amount',
				dataConfig: {
					type: 'field' as const,
					field: {
						fieldName: 'total',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: {
						type: 'theme' as const,
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'currency' as const,
			},
		],
		headers: [
			{ id: 'h6', text: 'Subtotal' },
			{ id: 'h7', text: 'Total' },
		],
		sortable: true,
		alignment: 'right' as const,
		verticalAlignment: 'top' as const,
	},
	{
		id: 'actions-info',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: {
					type: 'custom_text' as const,
					customText: '',
				},
				styleConfig: {
					size: 'md' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'actions_dropdown' as const,
				dropdownActions: [
					{
						id: 'close-action',
						label: 'Close',
						iconName: 'ban',
						callbackName: 'closeQuote',
						variant: 'primary',
						visibility: 'visible',
						requiredPermission: 'quotes:update',
						visibilityCallback: 'shouldShowCloseButton',
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'deleteQuote',
						variant: 'danger',
						visibility: 'visible',
						requiredPermission: 'quotes:delete',
					},
				],
			},
		],
		headers: [{ id: 'h8', text: 'Actions' }],
		sortable: false,
		width: 80,
		align: 'center' as const,
	},
];

const REQUIRED_FIELDS = [
	'_id',
	'_fk_client__id',
	'createdDate',
	'expirationDate',
	'sales_rep_name',
	'numberOfLines',
	'subtotal',
	'total',
	'status',
];

/**
 * Quotes embedded in client view configuration
 */
export const QUOTE_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-embedded-quotes-open',
			cacheTTL: 600000,
			name: 'Open Quotes',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			description: 'Open quotes that are still active and can be converted',
			defaultSortId: 'date-desc',
			requiredFields: REQUIRED_FIELDS,
			sortOrder: 0,
			filterRules: {
				quoteStatus: {
					defaultValue: [{ id: 'open', label: 'Open' }],
				},
			},
		},
		{
			id: 'client-embedded-quotes-needs-review',
			cacheTTL: 600000,
			name: 'Needs Review',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			description: 'Quotes that require review before processing',
			defaultSortId: 'date-desc',
			requiredFields: REQUIRED_FIELDS,
			sortOrder: 1,
			filterRules: {
				quoteStatus: {
					defaultValue: [{ id: 'needsReview', label: 'Needs Review' }],
				},
			},
		},
		{
			id: 'client-embedded-quotes-all',
			cacheTTL: 600000,
			name: 'All Quotes',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			description: 'Complete list of all quotes',
			defaultSortId: 'date-desc',
			requiredFields: REQUIRED_FIELDS,
			sortOrder: 2,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'childAccounts',
			'quoteKeywords',
			'quoteSalesRep',
			'quoteDateRange',
			'quoteStatus',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/quotes/:id',
			objectLabel: 'Quote',
			objectTable: 'order',
			selectedFields: [{ fieldName: '_id', fieldLabel: 'ID' }],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
			],
		},
	],
	showResetButton: true,
	tableSorts: [
		{
			id: 'date-asc',
			name: 'Date (Earliest First)',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'order',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'date-desc',
			name: 'Date (Latest First)',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'order',
					direction: 'DESC',
				},
			],
		},
		{
			id: 'total-asc',
			name: 'Total (Low to High)',
			fields: [
				{
					fieldName: 'total',
					objectTable: 'order',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'total-desc',
			name: 'Total (High to Low)',
			fields: [
				{
					fieldName: 'total',
					objectTable: 'order',
					direction: 'DESC',
				},
			],
		},
	],
};
